import { ChangeDetectionStrategy, Component, ElementRef, ViewEncapsulation, afterRenderEffect, computed, inject, input, signal, viewChild } from '@angular/core';
import type { UIMessage } from 'ai';
import { Attachment, AttachmentType } from '@jms/model';
import { WorkBuddySideDialogService } from '@workbuddy/ui/services';
import { AttachmentComponent } from '@workbuddy/ui/components/attachment';
import { AttachmentPreviewDialogComponent } from '@workbuddy/ui/components/attachment.preview.dialog';

import { AgentUtility } from '../../agent.utility';
import type { AnyPart } from '../../agent.types';
import { AgentMessageActionsComponent } from '../message.actions/message.actions.component';

// The jms-ai input processor compiles a non-previewable file into a model-facing "attachment note"
// text part, tagged with providerMetadata.workbuddy.attachmentNote. The UI hides that note and rebuilds
// a file chip from it keyed off the FLAG, never the note text — a user can legitimately type text that
// looks like the note. The regex is only a strict fallback for legacy notes persisted before the flag
// existed (requires BOTH markers, so real user text won't be mistaken for the note).
const ATTACHMENT_NOTE_PREFIX = /^(?:User attached file|A previous attachment) "/;

interface WorkbuddyNoteMetadata {
  attachmentNote?: boolean;
  filename?: string;
  mediaType?: string;
}

function attachmentNoteMetadata(part: AnyPart): WorkbuddyNoteMetadata | undefined {

  const provider = AgentUtility.partField<Record<string, WorkbuddyNoteMetadata> | undefined>(part, 'providerMetadata', undefined)
    ?? AgentUtility.partField<Record<string, WorkbuddyNoteMetadata> | undefined>(part, 'providerOptions', undefined);
  const note = provider?.['workbuddy'];
  return note?.attachmentNote ? note : undefined;
}

function isLegacyAttachmentNoteText(text: string): boolean {

  return ATTACHMENT_NOTE_PREFIX.test(text) && text.includes('uploads/uploaded_files.json');
}

function isAttachmentNotePart(part: AnyPart): boolean {

  if (attachmentNoteMetadata(part)) return true;
  return isLegacyAttachmentNoteText(AgentUtility.partField<string>(part, 'text', ''));
}

// Distinctive icons for well-known document types. Everything else (emails, json, xml, unknown,
// etc.) falls back to a neutral generic file icon.
const SPECIAL_FILE_ICONS: Record<string, string> = {
  '.pdf': 'fa-regular fa-file-pdf',
  '.csv': 'fa-regular fa-file-csv',
  '.xls': 'fa-regular fa-file-excel',
  '.xlsx': 'fa-regular fa-file-excel',
  '.xlsm': 'fa-regular fa-file-excel',
  '.doc': 'fa-regular fa-file-word',
  '.docx': 'fa-regular fa-file-word',
};
const GENERIC_FILE_ICON = 'fa-regular fa-file';

interface AttachmentTile {
  id: string;
  name: string;
  isThumbnail: boolean;
  icon: string;
  ext: string;
  attachment: Attachment;
}

function isAttachmentNoteText(text: string): boolean {

  return ATTACHMENT_NOTE_PREFIX.test(text) || text.includes('uploads/uploaded_files.json');
}

function buildAttachmentTile(name: string, url: string, mimeType: string, index: number): AttachmentTile {

  const isImage = AgentUtility.isImageType(mimeType);
  const isVideo = mimeType.startsWith('video/');
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
  const isPdf = ext === '.pdf' || mimeType === 'application/pdf';

  let type: AttachmentType = 'File';
  if (isImage) type = 'Image';
  else if (isVideo) type = 'Video';

  const attachment = {
    _id: url || name,
    type,
    path: url,
    title: name,
    fileName: name,
    thumbnail: isImage ? url : undefined,
  } as Attachment;

  return {
    // url-less chips (rebuilt from a note) share no url; disambiguate by index so two attachments with
    // the same display name don't collide on the @for track key or the equality comparator.
    id: url || `${name}#${index}`,
    name,
    // Only real file parts can produce a preview; url-less chips rebuilt from a note never do.
    isThumbnail: (isImage || isVideo || isPdf) && Boolean(url),
    icon: SPECIAL_FILE_ICONS[ext] ?? GENERIC_FILE_ICON,
    ext: ext.replace('.', '').toUpperCase(),
    attachment,
  };
}


@Component({
  selector: 'agent-user-message',
  standalone: true,
  imports: [AttachmentComponent, AgentMessageActionsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './user.message.component.html',
  styleUrls: ['./user.message.component.less'],
})
export class AgentUserMessageComponent {

  private readonly dialogService = inject(WorkBuddySideDialogService);

  public $message = input.required<UIMessage>({ alias: 'message' });
  /** Shows the copy action under the bubble. Only transcript messages turn it on; a bubble shown anywhere else stays bare. */
  public $copyable = input<boolean>(false, { alias: 'copyable' });
  public $showAttachment = input<boolean>(true, { alias: 'showAttachment' });

  // Local by design: expansion resets to collapsed whenever the message remounts
  // (thread switch, restore), so a remount can never strand a message expanded
  // with an unmeasured overflow state and no visible Show less button.
  public $isExpanded = signal(false);

  private readonly $textEl = viewChild<ElementRef<HTMLParagraphElement>>('textEl');

  // True when the clamped text actually overflows its 3-line preview — only then does the
  // Show more / Show less toggle render. Expansion is driven purely by that button; the
  // bubble itself is not clickable, so a stray click can no longer collapse a long message.
  public $overflows = signal(false);

  constructor() {

    // Measure after render, re-running when the text changes. Only measure while clamped:
    // expanded text never overflows, and keeping the collapsed measurement is what keeps
    // the "Show less" button visible while expanded.
    afterRenderEffect(() => {
      this.$textContent();
      if (this.$isExpanded()) return;
      const el = this.$textEl()?.nativeElement;
      this.$overflows.set(!!el && el.scrollHeight > el.clientHeight + 1);
    });
  }

  public $textContent = computed(() => {

    const parts = this.$message().parts ?? [];
    return parts
      // Drop the internal model-facing attachment note (by flag, with a strict legacy-text fallback);
      // it must never be shown to the user, and a user's own message must never be mistaken for it.
      .filter((p: AnyPart) => p.type === 'text' && !isAttachmentNotePart(p))
      .map((p: AnyPart) => AgentUtility.partField<string>(p, 'text', ''))
      .filter((text) => !!text)
      .join('\n');
  });

  // Display tiles for the message's file attachments. Images/videos/PDFs render as a real preview;
  // every other type renders as a uniform file chip (icon + name) so non-previewable files
  // (csv/eml/msg/json/...) are not shown as an empty card. The `equal` comparator keeps the SAME
  // array reference while a reply streams: the adapter re-clones the message object on every token,
  // but the file parts never change, so this stops the attachment cards flickering/reloading mid-stream.
  public $attachments = computed<AttachmentTile[]>(() => {

    const parts = this.$message().parts ?? [];

    const fileTiles = parts
      .filter((p: AnyPart) => p.type === 'file')
      .map((p: AnyPart, index) => {
        const mimeType = AgentUtility.partField<string>(p, 'mediaType', '') || AgentUtility.partField<string>(p, 'mimeType', '');
        const url = AgentUtility.partField<string>(p, 'url', '') || AgentUtility.partField<string>(p, 'data', '');
        return buildAttachmentTile(AgentUtility.displayFilename(p), url, mimeType, index);
      });

    // Restored messages lose their file part: the input processor compiled the non-previewable file
    // into a flagged note, and that note is what persists. Rebuild a chip from the note's metadata so
    // the user still sees a card. Flag-only (never from note text) so a user-typed lookalike can't
    // fabricate a chip for a file that was never sent.
    const seen = new Set(fileTiles.map((tile) => tile.name));
    const noteTiles = parts
      .filter((p: AnyPart) => p.type === 'text')
      .map((p: AnyPart) => attachmentNoteMetadata(p))
      .filter((meta): meta is WorkbuddyNoteMetadata => !!meta?.filename && !seen.has(meta.filename))
      .map((meta, index) => buildAttachmentTile(meta.filename as string, '', meta.mediaType ?? '', fileTiles.length + index));

    return [...fileTiles, ...noteTiles];
  // Compare only by stable identity (id/isThumbnail/name). Deliberately NOT by thumbnail: the shared
  // attachment component mutates attachment.thumbnail once it fetches a PDF preview, and keeping the
  // previous array reference preserves that fetched preview across streamed tokens.
  }, { equal: (a, b) => a.length === b.length && a.every((t, i) => t.id === b[i].id && t.isThumbnail === b[i].isThumbnail && t.name === b[i].name) });

  public $hasAttachments = computed(() => this.$showAttachment() && this.$attachments().length > 0);

  public onToggleExpand(): void {

    this.$isExpanded.update((expanded) => !expanded);
  }

  public onAttachmentClick(attachment: Attachment): void {

    this.dialogService.openPseudoPage(AttachmentPreviewDialogComponent, {
      closeOnEscape: true,
      closeOnBackdropClick: true,
      data: {
        attachments: this.$attachments().map((tile) => tile.attachment),
        attachmentId: attachment._id,
        title: attachment.fileName ?? '',
      },
    });
  }
}
